/**
 * connectionRouting — every decision that depends on WHICH backend a query is
 * going to.
 *
 * These were previously inlined at each call site, and each site drifted
 * independently. The bugs that produced, all shipped:
 *
 *   - executeQuery built credentials without the backend selector, so WMS
 *     queries were sent down the BI Publisher path and failed as auth errors.
 *   - runQuery ran an Oracle syntax pre-flight over WMS SQL and rejected valid
 *     queries, because it never asked what backend the tab pointed at.
 *   - The schema was cached under "loaded / not loaded" rather than under the
 *     connection it described, so switching backends kept the other system's
 *     tables.
 *
 * They are pure functions here so they can be tested without a DOM, and so
 * there is one answer to each question instead of one per caller.
 */

export const FUSION = 'fusion_bip';
export const WMS    = 'wms_lgfapi';

/** Connections saved before multi-backend support have no type; they are Fusion. */
export function backendOf(conn) {
  return conn?.type === WMS ? WMS : FUSION;
}

export function isWmsConnection(conn) {
  return backendOf(conn) === WMS;
}

/**
 * Which connection a given tab runs against. A tab may override the active
 * connection, so this must never be answered from the global config.
 */
export function resolveConnection({ tabs, connections, defaultConnectionId, tabId }) {
  const list = Array.isArray(connections) ? connections : [];
  const tab  = (Array.isArray(tabs) ? tabs : []).find(t => t?.id === tabId) || (tabs || [])[0];
  const id   = tab?.connectionId || defaultConnectionId;
  return list.find(c => c?.id === id) || list[0] || null;
}

/**
 * The credential bundle sent to /api/bip/*. `connectionType` is what the server
 * dispatches on; omitting it silently routes the request to the wrong backend,
 * which is why it lives here rather than being spread by hand at each caller.
 */
export function credentialsFor(conn) {
  return {
    baseUrl:        conn?.baseUrl  || '',
    username:       conn?.username || '',
    password:       conn?.password || '',
    connectionType: backendOf(conn),
    env:            conn?.env      || '',
    facility:       conn?.facility || '',
  };
}

/**
 * Identity of what a cached schema describes. Includes the backend and the env
 * so two connections sharing a host still count as different namespaces.
 */
export function connectionKey(conn) {
  return [backendOf(conn), conn?.baseUrl || '', conn?.env || '', conn?.username || ''].join('|');
}

/** Enough of a connection present to fetch anything at all. */
export function isUsable(conn) {
  return !!(conn?.baseUrl && conn?.username && conn?.password);
}

// ── capability gates ─────────────────────────────────────────────────────────

/**
 * The Oracle SQL pre-flight is an LLM asked to judge Oracle syntax. WMS SQL is
 * a different grammar, and the compiler validates it deterministically on the
 * server, so running it here only produces false rejections and a wasted call.
 */
export function usesOraclePreflight(conn) {
  return !isWmsConnection(conn);
}

/**
 * The Data Dictionary describes Oracle Fusion tables. On a WMS connection it
 * would answer confidently about tables the pod does not have.
 */
export function usesDataDictionary(conn) {
  return !isWmsConnection(conn);
}

/** BI Publisher leaves temp catalog objects behind; lgfapi has no catalog. */
export function usesTempCleanup(conn) {
  return !isWmsConnection(conn);
}

/** A WMS connection is addressed by env, so it cannot be saved without one. */
export function isComplete(conn) {
  if (!conn?.name?.trim() || !conn?.baseUrl?.trim() || !conn?.username?.trim() || !conn?.password?.trim()) {
    return false;
  }
  return !isWmsConnection(conn) || !!conn?.env?.trim();
}
